import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Search,
  Users,
  User,
  Camera,
  ScanFace,
  Loader2,
  Trash2,
  Merge,
} from 'lucide-react';

import { usePersons, useDeletePerson, useMergePersons } from '@/api/hooks/useFaces';
import type { Person } from '@/api/types/face.types';

function PersonCard({
  person,
  isSelected,
  onSelect,
}: {
  person: Person;
  isSelected: boolean;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <Link
      to={`/people/${person.id}`}
      className={`card group block transition-all hover:shadow-md ${
        isSelected ? 'ring-2 ring-primary-500 ring-offset-2 dark:ring-offset-gray-900' : ''
      }`}
      onClick={(e) => {
        if (e.shiftKey || e.ctrlKey || e.metaKey) {
          e.preventDefault();
          onSelect(person.id);
        }
      }}
    >
      <div className="flex items-center gap-4">
        {/* Avatar */}
        {person.representative_face_url ? (
          <img
            src={person.representative_face_url}
            alt={person.name || t('faces.unknownPerson')}
            className="h-16 w-16 flex-shrink-0 rounded-full object-cover ring-2 ring-white dark:ring-gray-800"
          />
        ) : (
          <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
            <User className="h-8 w-8 text-gray-400" />
          </div>
        )}

        {/* Info */}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-gray-900 group-hover:text-primary-600 dark:text-gray-100 dark:group-hover:text-primary-400">
            {person.name || t('faces.unknownPerson')}
          </h3>
          {person.role && person.role !== 'unknown' && (
            <span className="mt-0.5 inline-block rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
              {t(`faces.roles.${person.role}`)}
            </span>
          )}
          <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <ScanFace className="h-3 w-3" />
              {t('faces.faceCountShort', { count: person.face_count })}
            </span>
            <span className="flex items-center gap-1">
              <Camera className="h-3 w-3" />
              {t('faces.photoCountShort', { count: person.photo_count })}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function PersonDirectoryPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event') || '';
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data, isLoading } = usePersons({
    event: eventId,
    search: search || undefined,
    ordering: '-face_count',
  });

  const deletePerson = useDeletePerson();
  const mergePersons = useMergePersons();

  const persons = data?.results ?? [];

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleMerge = () => {
    const ids = Array.from(selectedIds);
    if (ids.length !== 2) return;
    mergePersons.mutate(
      { person_id_a: ids[0], person_id_b: ids[1] },
      { onSuccess: () => setSelectedIds(new Set()) },
    );
  };

  const handleDelete = () => {
    selectedIds.forEach((id) => deletePerson.mutate(id));
    setSelectedIds(new Set());
  };

  if (!eventId) {
    return (
      <div className="page-container">
        <h1 className="page-title">{t('nav.people')}</h1>
        <div className="mt-6">
          <div className="card mx-auto max-w-lg text-center">
            <Users className="mx-auto mb-3 h-10 w-10 text-gray-400" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('faces.selectEventFirst')}
            </p>
            <Link to="/events" className="btn-primary mt-4 inline-block">
              {t('faces.goToEvents')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="flex items-center justify-between">
        <h1 className="page-title">{t('nav.people')}</h1>

        <div className="flex items-center gap-3">
          {/* Bulk actions */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                {t('gallery.selected', { count: selectedIds.size })}
              </span>
              {selectedIds.size === 2 && (
                <button
                  onClick={handleMerge}
                  disabled={mergePersons.isPending}
                  className="flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
                >
                  <Merge className="h-3 w-3" />
                  {t('faces.merge')}
                </button>
              )}
              <button
                onClick={handleDelete}
                className="flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
              >
                <Trash2 className="h-3 w-3" />
                {t('actions.delete')}
              </button>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('actions.search')}
              className="input-field ps-10"
            />
          </div>
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : persons.length === 0 ? (
          <div className="card mx-auto max-w-md text-center">
            <Users className="mx-auto mb-3 h-10 w-10 text-gray-400" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {search ? t('status.noResults') : t('faces.noPeopleYet')}
            </p>
            {!search && (
              <Link
                to={`/faces?event=${eventId}`}
                className="btn-primary mt-4 inline-flex items-center gap-2"
              >
                <ScanFace className="h-4 w-4" />
                {t('faces.runFaceWizard')}
              </Link>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {persons.map((person) => (
              <PersonCard
                key={person.id}
                person={person}
                isSelected={selectedIds.has(person.id)}
                onSelect={toggleSelect}
              />
            ))}
          </div>
        )}

        {/* Hint */}
        {persons.length > 0 && (
          <p className="mt-4 text-center text-xs text-gray-400">
            {t('faces.multiSelectHint')}
          </p>
        )}
      </div>
    </div>
  );
}
